import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  return (
    <div className="font-satoshi max-w-6xl mx-auto px-6 py-14 text-gray-800">
      <button
        onClick={() => navigate("/")}
        className="absolute top-4 left-4 text-gray-600 border border-gray-300 rounded-full p-2 hover:text-[#21A6E9] hover:bg-gray-100 transition-all duration-200"
        aria-label="Back"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>

      <div className="flex justify-between mb-8">
        <h1 className="text-3xl font-bold mb-6 text-center">Privacy Policy</h1>

        <p className="text-base text-center mt-1 -ml-2">
          Effective Date: 01/05/2025
        </p>
      </div>

      <p className="mb-6  space-y-1 text-base">
        This Privacy Policy outlines how{" "}
        <strong>BCV Claimpro Assist Pvt. Ltd.</strong> ("Company", "we", "us",
        or "our") collects, uses, stores, shares, and protects your personal
        data when you access or use our website, mobile application, or avail
        any services through our platform ("Platform"). By accessing or using
        our Platform, you agree to the practices described in this policy.
      </p>

      {/* Section 1 */}
      <section className="mb-6 space-y-1 text-base">
        <h2 className="text-xl text-[#21A6E9] font-semibold mb-2">
          1. Applicability
        </h2>
        <p className="text-base">
          This Policy applies to all users ("you", "your") who access or use the
          Platform, including:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-1 text-base">
          <li>Visitors</li>
          <li>Registered users</li>
          <li>Third-party service providers</li>
        </ul>
        <p className="mt-2">
          It is governed by Indian laws including the{" "}
          <strong>Information Technology Act, 2000</strong>, and the{" "}
          <strong>DPDPA, 2023</strong> (once enforced).
        </p>
      </section>

      {/* Section 2 */}
      <section className="mb-6 space-y-1 text-base">
        <h2 className="text-xl text-[#21A6E9] font-semibold mb-2">
          2. Information We Collect
        </h2>
        <h3 className="text-sm">a. Personal Identifiable Information (PII)</h3>
        <ul className="list-disc pl-6 mt-1 space-y-1">
          <li>Full Name</li>
          <li>Date of Birth</li>
          <li>Contact Information (email, phone number)</li>
          <li>Address (residential or correspondence)</li>
          <li>Identity proof documents (PAN, Aadhaar, etc.)</li>
        </ul>
        <h3 className="text-sm mt-3">b. Transactional Information</h3>
        <ul className="list-disc pl-6 mt-1 space-y-1">
          <li>Details of claims raised and processed</li>
          <li>
            Payment details (excluding sensitive card data unless encrypted)
          </li>
        </ul>
        <h3 className="text-sm mt-3">c. Technical and Usage Data</h3>
        <ul className="list-disc pl-6 mt-1 space-y-1">
          <li>IP address, browser type, device info</li>
          <li>Cookies and session data</li>
          <li>Location data (with permission)</li>
        </ul>
        <h3 className="text-sm mt-3">
          d. Sensitive Personal Data or Information (SPDI)
        </h3>
        <ul className="list-disc pl-6 mt-1 space-y-1">
          <li>Financial information (bank account, payment details)</li>
        </ul>
      </section>

<section className="mb-6 space-y-1 text-base">
  <h2 className="text-xl text-[#21A6E9] font-semibold mb-2">
    3. Location Data
  </h2>
  <p>
    We may collect and access your device’s location information in order to provide specific features and improve user experience. This location data may be collected in the foreground (while the app is in use) and/or in the background (when the app is closed or not actively in use), depending on the permissions granted by you.
  </p>

  <h3 className="text-lg font-semibold mt-4">How We Use Location Data</h3>
  <ul className="list-disc pl-6 space-y-1">
    <li>
      Verifying your location to provide relevant services (e.g., vehicle assistance, nearby support providers, service coverage, etc.)
    </li>
    <li>
      Enhancing service accuracy (e.g., real-time updates, dispatch, route optimization)
    </li>
    <li>
      Safety, security, and fraud prevention
    </li>
  </ul>

  <p className="mt-2">
    We do not share your location data with third parties except as required to deliver core services or comply with legal obligations. Location data is stored securely and retained only as long as necessary to fulfill the purpose for which it was collected.
  </p>
</section>

      {/* Section 3 */}
      <section className="mb-6 space-y-1 text-base">
        <h2 className="text-xl text-[#21A6E9] font-semibold mb-2">
          4. Purpose of Collection
        </h2>
        <ul className="list-disc pl-6 space-y-1 text-base">
          <li>To provide and improve our services</li>
          <li>To process transactions and claims</li>
          <li>To verify user identity and prevent fraud</li>
          <li>To communicate updates or promotional content</li>
          <li>To comply with legal obligations</li>
          <li>For internal analytics and reporting</li>
        </ul>
      </section>

      {/* Section 4 */}
      <section className="mb-6 space-y-1 text-base">
        <h2 className="text-xl text-[#21A6E9] font-semibold mb-2">
          5. Consent
        </h2>
        <p>
          By using our Platform, you consent to our collection and processing of
          your data. You may withdraw your consent by contacting us, though this
          may limit your access to some services.
        </p>
      </section>

      {/* Section 5 */}
      <section className="mb-6 space-y-1 text-base">
        <h2 className="text-xl text-[#21A6E9] font-semibold mb-2">
          6. Disclosure of Information
        </h2>
        <p>We may share your information with:</p>
        <ul className="list-disc pl-6 space-y-1 text-base">
          <li>Insurance companies or TPA partners</li>
          <li>Payment gateways</li>
          <li>IT/back-end service providers (under confidentiality)</li>
          <li>Government bodies (when legally required)</li>
          <li>Any successor entity (in case of M&A)</li>
        </ul>
        <p className="mt-2">
          We do <strong>not</strong> sell or rent your personal data.
        </p>
      </section>

      {/* Section 6 */}
      <section className="mb-6 space-y-1 text-base">
        <h2 className="text-xl text-[#21A6E9] font-semibold mb-2">
          7. Data Retention
        </h2>
        <ul className="list-disc pl-6 space-y-1 text-base">
          <li>As long as needed for services</li>
          <li>As required under law</li>
          <li>For dispute resolution and enforcement</li>
        </ul>
      </section>

      {/* Section 7 */}
      <section className="mb-6 space-y-1 text-base">
        <h2 className="text-xl text-[#21A6E9] font-semibold mb-2">
          8. Data Security
        </h2>
        <p>We follow security practices under IT Rules, 2011:</p>
        <ul className="list-disc pl-6 space-y-1 text-base">
          <li>SSL encryption</li>
          <li>Regular vulnerability scans</li>
          <li>Role-based access controls</li>
          <li>Secure servers and firewalls</li>
        </ul>
        <p className="mt-2">
          Note: No method of internet transmission is 100% secure. Keep your
          credentials confidential.
        </p>
      </section>

      {/* Section 8 */}
      <section className="mb-6 space-y-1 text-base">
        <h2 className="text-xl text-[#21A6E9] font-semibold mb-2">
          9. User Rights
        </h2>
        <p>You have the right to:</p>
        <ul className="list-disc pl-6 space-y-1 text-base">
          <li>Access or update your information</li>
          <li>Withdraw consent</li>
          <li>Request deletion (subject to legal requirements)</li>
          <li>File complaints with Data Protection Board (once formed)</li>
        </ul>
        <p className="mt-2">
          Email:{" "}
          <a href="mailto:info@claimpro.in" className="text-blue-600 underline">
            info@claimpro.in
          </a>
        </p>
      </section>

      {/* Section 9 */}
      <section className="mb-6 space-y-1 text-base">
        <h2 className="text-xl text-[#21A6E9] font-semibold mb-2">
          10. Cookies
        </h2>
        <p>
          We use cookies to improve your experience. You may manage them via
          browser settings.
        </p>
      </section>

      {/* Section 10 */}
      <section className="mb-6 space-y-1 text-base">
        <h2 className="text-xl text-[#21A6E9] font-semibold mb-2">
          11. Children’s Privacy
        </h2>
        <p>
          We do not knowingly collect data from users under 18 without parental
          consent. If found, we delete it.
        </p>
      </section>

      {/* Section 11 */}
      <section className="mb-6 space-y-1 text-base">
        <h2 className="text-xl text-[#21A6E9] font-semibold mb-2">
          12. Cross-Border Data Transfer
        </h2>
        <p>
          If your data is transferred outside India, we ensure it complies with
          Indian laws and safeguards your rights.
        </p>
      </section>

      {/* Section 12 */}
      <section className="mb-6 space-y-1 text-base">
        <h2 className="text-xl text-[#21A6E9] font-semibold mb-2">
          13. Changes to this Policy
        </h2>
        <p>
          We may update this Privacy Policy. The latest version will be
          published with the effective date. Continued use of the Platform
          implies acceptance.
        </p>
      </section>

      {/* Section 13 */}
      {/* <section>
        <h2 className="text-xl text-[#21A6E9] font-semibold mb-2">13. Contact Us</h2>
        <p>
          <strong>BCV Claimpro Assist Pvt. Ltd.</strong><br />
          Address: G-236, 2nd Floor, Sector 57, Gurgaon – 122003, Haryana, India<br />
          Email: <a href="mailto:info@claimpro.in" className="text-blue-600 underline">info@claimpro.in</a><br />
          Working Hours: Monday to Saturday, 10:00 AM to 6:00 PM IST
        </p>
      </section> */}

      <section className="mb-6">
        <h2 className="text-xl text-[#21A6E9] font-semibold mb-2">
         14. Contact Us
        </h2>
        <div className="space-y-1 text-base">
          <div>
            <strong>BCV Claimpro Assist Pvt. Ltd.</strong>
          </div>
          <div>
            Address: G-236, 2nd Floor, Sector 57, Gurgaon – 122003, Haryana,
            India
          </div>
          <div>
            Email:{" "}
            <a
              href="mailto:info@claimpro.in"
              className="!text-[#21A6E9] underline  font-medium hover:!text-[#21A6E9] visited:!text-[#21A6E9]"
            >
              info@claimpro.in
            </a>
          </div>
          <div>Working Hours: Monday to Saturday, 10:00 AM to 6:00 PM IST</div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
